/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useTranslation} from "react-i18next";
import {padMinutes, translate} from "utils/utility";
import React from "react";
import {PackagesCartItemSlot} from "types/entities/PackagesCartItemSlot";
import {UsersIcon} from "@heroicons/react/24/outline";
import {ActivityType} from "types/enums/ActivityType";
import {DEFAULT_HALL} from "helpers/config";
import {HallCode} from "types/enums/HallCode";
import {PackageCategory} from "types/enums/PackageCategory";
import {
    moduloMinutesForFakeTimeslots,
    useGetAccessNamesByTimeslotsAndPackage,
    useGetRaceNamesByTimeslotsAndPackage,
} from "utils/timeslotUtils";
import {useTypedSelector} from "helpers/reducers";
import clsx from "clsx";

interface SlotItemProps {
    itemSlot: PackagesCartItemSlot;
    showPersonsCount: boolean;
    personsCount: number;
    index: number;
    className?: string;
    classNameItem?: string;
    newStyle?: boolean;
}

export const BookingRecapitulationItem = (props: {
    activityRoom?: string | undefined | null | boolean;
    date: string;
    itemIndex: number | null;
    name: string;
    personsCount: number;
    time: string;
    className?: string;
    newStyle?: boolean;
}) => {
    return (
        <div className={clsx("BookingRecapitulationItem w-full grid grid-cols-[3fr,1fr,1fr]", props.newStyle ? "even:bg-gray-50 border-b border-b-gray-200/50 last:border-b-0" : "space-x-1 text-xs sm:text-base", props.className)}>
            <div className={clsx(props.newStyle && "inline-flex items-center pr-1.5 pl-3 py-2")}>
                {props.name} {props.itemIndex ? "#" + props.itemIndex : ""}
            </div>
            <div className={clsx(props.newStyle && "inline-flex items-center px-1.5 py-2")}>{props.time}</div>
            {props.personsCount > 0 && (
                <b className={clsx("flex flex-row justify-end items-center space-x-1", props.newStyle ? "pl-1.5 pr-3 py-2" : "mx-4")}>
                    <span>{props.personsCount}</span>
                    {!props?.newStyle && <UsersIcon className={"w-4 h-4 text-gray-800"} />}
                </b>
            )}
        </div>
    );
};

export const BookingRecapitulationSlotItem = (props: SlotItemProps) => {
    const {t} = useTranslation();
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    if (props.itemSlot.persons_count === 0) {
        return null;
    }
    const fakeTimeslotMinutes = selectedPackage && selectedPackage.category === PackageCategory.Event && DEFAULT_HALL !== HallCode.Zwolle;
    // const fakeTimeslotMinutes = false;

    const getRaceNamesByTimeslotsAndPackage = useGetRaceNamesByTimeslotsAndPackage(props.index, selectedPackage);
    return (
        <div key={props.itemSlot.id} className={clsx("BookingRecapitulationSlotItem", props.className)}>
            <BookingRecapitulationItem
                activityRoom={
                    props.itemSlot.timeslot &&
                    props.itemSlot.timeslot?.activity?.type === ActivityType.TheParkPlayground &&
                    props.itemSlot.timeslot?.activityRoom.name
                }
                className={props.classNameItem}
                date={props.itemSlot.date}
                itemIndex={props.index + 1}
                name={DEFAULT_HALL === HallCode.ArboPark ? translate(props.itemSlot.packageItem?.name) : getRaceNamesByTimeslotsAndPackage}
                newStyle={props.newStyle}
                personsCount={props.showPersonsCount ? props.personsCount : props.itemSlot.persons_count}
                time={
                    !fakeTimeslotMinutes
                        ? props.itemSlot.hour + ":" + padMinutes(props.itemSlot.minute)
                        : moduloMinutesForFakeTimeslots(props.itemSlot.timeslot)
                }
            />
        </div>
    );
};
export const BookingRecapitulationAccessSlotItem = (props: SlotItemProps) => {
    const {t} = useTranslation();
    if (props.itemSlot.persons_count === 0) {
        return null;
    }
    const getRaceNamesByTimeslotsAndPackage = useGetAccessNamesByTimeslotsAndPackage(props.index, null);
    const formattedTime = () => props.itemSlot.hour + ":" + padMinutes(props.itemSlot.minute);
    const itemCount = props.showPersonsCount ? props.personsCount : props.itemSlot.persons_count;
    return (
        <div key={props.itemSlot.id} className={clsx("BookingRecapitulationAccessSlotItem", props.className)}>
            <BookingRecapitulationItem
                activityRoom={null}
                className={props.classNameItem}
                date={props.itemSlot.date}
                itemIndex={null}
                name={t("Reservation") + " - " + getRaceNamesByTimeslotsAndPackage}
                newStyle={props.newStyle}
                personsCount={itemCount}
                time={formattedTime()}
            />
        </div>
    );
};
